import React, { useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
    List,
    Datagrid,
    TextField,
    BooleanField,
    DateField,
    FilterButton,
    ExportButton,
    TopToolbar,
    TextInput,
    BooleanInput,
    DateTimeInput,
    DateInput,
    SelectInput,
    FunctionField,
    useRecordContext,
    useListContext,
    required
} from 'react-admin';
import { Card, CardContent, Typography, Box, Chip } from '@mui/material';
import { config_strings } from '../config/config';

const ListActions = () => (
    <TopToolbar>
        <ExportButton />
    </TopToolbar>
);

const verificationFilters = [
    <DateInput source="start_time" label="From" alwaysOn />,
    <DateInput source="end_time" label="To" alwaysOn />,
    <TextInput source="uid" label="UID" alwaysOn />,
    <SelectInput 
        source="is_tampered" 
        label="Tampering Status" 
        choices={[
            { id: 'both', name: 'All' },
            { id: 'false', name: 'Not Tampered' },
            { id: 'true', name: 'Tampered' }
        ]}
        validate={required()}
        alwaysOn 
    />,
];

const StatusField = () => {
    const record = useRecordContext();
    if (!record) return null;
    
    return (
        <Box display="flex" gap={1}>
            <Chip
                label={record.is_valid ? 'Valid' : 'Invalid'}
                color={record.is_valid ? 'success' : 'error'}
                size="small"
            />
            <Chip
                label={record.is_tt_closed ? 'Tamper Secure' : 'Tampered'}
                color={record.is_tt_closed ? 'success' : 'error'}
                size="small"
            />
        </Box>
    );
};

const IntroSection = () => (
    <Card sx={{ marginBottom: 2 }}>
        <CardContent>
            <Typography variant="h5" gutterBottom>
                { config_strings.intros.verification.title }
            </Typography>
            <Typography variant="body1" color="textSecondary">
                { config_strings.intros.verification.text }
            </Typography>
        </CardContent>
    </Card>
);

const TruncatedDataField = ({ source, chars = 3 }) => {
    const record = useRecordContext();
    if (!record) return null;

    const value = record[source];
    if (!value) return null;

    return (
        <Box sx={{ fontFamily: 'monospace', fontSize: '0.575rem' }}>
            {value.length > (chars * 2 + 3) 
                ? `${value.slice(0, chars)}...${value.slice(-chars)}`
                : value
            }
        </Box>
    );
};

const VerificationsListView = () => {
    const { data, isLoading, error, filterValues, setFilters } = useListContext();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Set default date filters programmatically if not already set
    useEffect(() => {
        if (!filterValues.start_time || !filterValues.end_time) {
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
            const now = new Date();
            
            const formatDate = (date) => date.toISOString().slice(0, 10);
            
            setFilters({
                ...filterValues,
                start_time: formatDate(thirtyDaysAgo),
                end_time: formatDate(now)
            });
        }
    }, [filterValues, setFilters]);

    if (isLoading) {
        return <div>Loading...</div>;
    }

    if (error) {
        return <div>Error: {error.message}</div>;
    }

    if (!data || data.length === 0) {
        return <div>No records found</div>;
    }

    // Render our actual data table
    return (
        <Box sx={{ width: '100%', maxWidth: '100vw', overflow: 'hidden' }}>
            <Box sx={{ p: { xs: 0, sm: 2 } }}>
                <IntroSection />
                <Card>
                    <Datagrid size="small">
                        <StatusField label="NFC Tag Status" />
                        <FunctionField
                            label="Wine"
                            render={record => record.metadata?.name || 'N/A'}
                        />
                        <FunctionField
                            label="Batch"
                            render={record => record.nft?.contract_symbol || 'N/A'}
                        />
                        <DateField 
                            source="timestamp" 
                            label="Time"
                            showTime 
                            options={{ 
                                day: 'numeric',
                                month: 'short',
                                hour: 'numeric',
                                minute: 'numeric',
                                hour12: false
                            }}
                        />
                        <FunctionField
                            source="location_city"
                            label="City"
                            render={record => (
                                <Box display="flex" alignItems="center" gap={0.5}>
                                    <span>{record.location_city || 'Unknown'}</span>
                                    {typeof record.gps_lat === 'number' && typeof record.gps_lon === 'number' && (
                                        <Chip
                                            label="GPS"
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                            sx={{ height: 18, fontSize: '0.625rem' }}
                                        />
                                    )}
                                </Box>
                            )}
                        />
                        <FunctionField
                            source="location_country"
                            label="Country"
                            render={record => record.location_country || 'Unknown'}
                        />
                        {!isMobile && (
                            <>
                                <TruncatedDataField source="uid" label="UID" chars={16} />
                                <TruncatedDataField source="cmac" label="CMAC" chars={16} />
                                <TruncatedDataField source="p" label="PICC Data" chars={6} />
                            </>
                        )}
                    </Datagrid>
                </Card>
            </Box>
        </Box>
    );
};

export const VerificationsList = () => {
    return (
        <List
            actions={<ListActions />}
            filters={verificationFilters}
            filterDefaultValues={{
                is_tampered: 'both'
            }}
            hasCreate={false}
            hasEdit={false}
            sort={{ field: 'timestamp', order: 'DESC' }}
            perPage={50}
            filter={{ 
                account_id: localStorage.getItem('auth') 
                    ? JSON.parse(localStorage.getItem('auth')).tenant 
                    : null
            }}
        >
            <VerificationsListView />
        </List>
    );
};
export default VerificationsList;